import { canonicalJson } from './pack-engine';

export type PrudentialStandardKey = 'FINANCIAL_PRUDENTIAL_MANAGEMENT' | 'LIQUIDITY' | 'INVESTMENT';
export type PrudentialApplicability = 'APPLIES' | 'DOES_NOT_APPLY' | 'UNKNOWN_SHOW_ANYWAY';
export type PrudentialProviderCategory = 'DEPOSIT_HOLDER' | 'NO_CONFIRMED_DEPOSITS';

export interface PrudentialProviderProfile {
  providerCategory?: 'RESIDENTIAL' | 'HOME_CARE' | 'MIXED' | null;
  /** Observed active refundable deposit record, regardless of registration mix. */
  holdsRefundableDeposits?: boolean | null;
}

export interface PrudentialMatrixRow {
  providerCategory: PrudentialProviderCategory;
  standardKey: PrudentialStandardKey;
  applicability: PrudentialApplicability;
  citation: string;
  unverified: true;
  /** Recorded provider decision reference; included in immutable pack digest. */
  decisionId?: string | null;
  decisionReasonDigest?: string | null;
  suggestedValue?: PrudentialApplicability;
}

export const PRUDENTIAL_STANDARDS: readonly PrudentialStandardKey[] = [
  'FINANCIAL_PRUDENTIAL_MANAGEMENT',
  'LIQUIDITY',
  'INVESTMENT',
];

const citationByStandard: Record<PrudentialStandardKey, string> = {
  FINANCIAL_PRUDENTIAL_MANAGEMENT:
    'Financial and Prudential Standards 2025, Part 2 — application clause UNVERIFIED',
  LIQUIDITY: 'Financial and Prudential Standards 2025, Part 3 — application clause UNVERIFIED',
  INVESTMENT: 'Financial and Prudential Standards 2025, Part 4 — application clause UNVERIFIED',
};

/** The Part 3/4 deposit-holder result is a review suggestion until the clauses are verified. */
export const PRUDENTIAL_APPLICABILITY_MATRIX: readonly PrudentialMatrixRow[] = [
  ...PRUDENTIAL_STANDARDS.map((standardKey) => ({
    providerCategory: 'DEPOSIT_HOLDER' as const,
    standardKey,
    applicability: (standardKey === 'FINANCIAL_PRUDENTIAL_MANAGEMENT'
      ? 'UNKNOWN_SHOW_ANYWAY'
      : 'APPLIES') as PrudentialApplicability,
    citation: citationByStandard[standardKey],
    unverified: true as const,
  })),
  ...PRUDENTIAL_STANDARDS.map((standardKey) => ({
    providerCategory: 'NO_CONFIRMED_DEPOSITS' as const,
    standardKey,
    applicability: 'UNKNOWN_SHOW_ANYWAY' as const,
    citation: citationByStandard[standardKey],
    unverified: true as const,
  })),
];

/** An observed refundable deposit takes precedence over registration categories. */
export function resolvePrudentialApplicability(
  profile: PrudentialProviderProfile,
): PrudentialMatrixRow[] {
  const category =
    profile.holdsRefundableDeposits === true ? 'DEPOSIT_HOLDER' : 'NO_CONFIRMED_DEPOSITS';
  return PRUDENTIAL_APPLICABILITY_MATRIX.filter((row) => row.providerCategory === category);
}

export interface PrudentialRecordedDecision {
  id: string;
  standard_key: PrudentialStandardKey;
  suggested_value: PrudentialApplicability;
  decided_value: PrudentialApplicability | null;
  decision_reason: string | null;
}

/** Snapshot the provider's value and its evidence reference without exposing the reason. */
export function applyPrudentialProviderDecisions(
  suggestions: readonly PrudentialMatrixRow[],
  decisions: readonly PrudentialRecordedDecision[],
  digestReason: (reason: string) => string,
): PrudentialMatrixRow[] {
  if (decisions.length !== suggestions.length) throw new Error('prudential_decisions_incomplete');
  return suggestions.map((suggestion) => {
    const matches = decisions.filter(
      (decision) => decision.standard_key === suggestion.standardKey,
    );
    if (matches.length !== 1 || !matches[0]) throw new Error('prudential_decisions_incomplete');
    const decision = matches[0];
    if (decision.decided_value && (!decision.id || !decision.decision_reason))
      throw new Error('prudential_decision_reference_incomplete');
    return {
      ...suggestion,
      applicability: decision.decided_value ?? decision.suggested_value,
      suggestedValue: decision.suggested_value,
      decisionId: decision.decided_value ? decision.id : null,
      decisionReasonDigest: decision.decided_value ? digestReason(decision.decision_reason!) : null,
    };
  });
}

export interface PrudentialEvidenceLink {
  register:
    | 'ACCOMMODATION_AGREEMENT'
    | 'ACCOMMODATION_DEPOSIT'
    | 'ACFR'
    | 'QFR'
    | 'GOVERNANCE_RECORD';
  recordId: string;
  reference: string;
  standardKeys: readonly PrudentialStandardKey[];
}

export interface BuildPrudentialEvidencePackInput {
  asOf: string;
  period: { start: string; end: string };
  applicability: readonly PrudentialMatrixRow[];
  links: readonly PrudentialEvidenceLink[];
  templateVersion: string;
  legalSourceVersion: string;
  sha256Hex: (canonical: string) => string;
}

export interface PrudentialEvidencePack {
  kind: 'PRUDENTIAL_EVIDENCE';
  manifest: Omit<BuildPrudentialEvidencePackInput, 'links' | 'sha256Hex'>;
  sections: readonly {
    standardKey: PrudentialStandardKey;
    applicability: PrudentialApplicability;
    citation: string;
    unverified: true;
    links: readonly PrudentialEvidenceLink[];
  }[];
  contentDigest: string;
}

const compare = (left: string, right: string) => left.localeCompare(right);

/** Builds a bounded identifier-only projection; changed inputs create a new immutable version. */
export function buildPrudentialEvidencePack(
  input: BuildPrudentialEvidencePackInput,
): PrudentialEvidencePack {
  if (!input.period.start || !input.period.end || input.period.end < input.period.start)
    throw new Error('prudential_evidence_period_invalid');
  const applicability = [...input.applicability].sort((a, b) =>
    compare(a.standardKey, b.standardKey),
  );
  if (
    applicability.length !== PRUDENTIAL_STANDARDS.length ||
    new Set(applicability.map((row) => row.standardKey)).size !== PRUDENTIAL_STANDARDS.length
  )
    throw new Error('prudential_evidence_applicability_incomplete');
  const links = [...input.links].sort(
    (a, b) => compare(a.register, b.register) || compare(a.recordId, b.recordId),
  );
  const sections = applicability.map((row) => ({
    standardKey: row.standardKey,
    applicability: row.applicability,
    citation: row.citation,
    unverified: true as const,
    links: links.filter((link) => link.standardKeys.includes(row.standardKey)),
  }));
  const draft: Omit<PrudentialEvidencePack, 'contentDigest'> = {
    kind: 'PRUDENTIAL_EVIDENCE',
    manifest: {
      asOf: input.asOf,
      period: input.period,
      applicability,
      templateVersion: input.templateVersion,
      legalSourceVersion: input.legalSourceVersion,
    },
    sections,
  };
  const { asOf: _asOf, ...digestManifest } = draft.manifest;
  return {
    ...draft,
    contentDigest: input.sha256Hex(canonicalJson({ ...draft, manifest: digestManifest })),
  };
}
